import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Dialog, DialogContent, DialogActions, Grid } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store/store.mobx';
import useSession from 'core/hooks/use-session';
import viewTask from 'core/assets/svg/view-task-white.svg';
import documentApprovalIcon from 'core/assets/svg/document-approval.svg';
import useStyles from '../footer.styles';

const FarmUnderwritingReviewPageFooter = observer(({ goToUWForm }) => {
  const { offer: store } = useStore();
  const classes = useStyles();
  const { canEditOffers } = useSession();

  return (
    <>
      <div>
        <Label className={classes.title} type="subtitle">
          We'll ask a few quick questions about the farm
        </Label>
        <Label className={classes.subtitle} type="subtitle">
          This helps confirm that the farm meets eligibility requirements. It only takes a few seconds.
        </Label>
      </div>
      <Grid container justifyContent="center" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          className={classes.wideBtn}
          disabled={store.loading || !canEditOffers}
          onClick={goToUWForm}
        >
          <Label className={classes.btnLabel}>Continue to Underwriting Review</Label>
        </Button>
      </Grid>
    </>
  );
});

const SendToUnderwritingConfirmModal = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogContent className={classes.modalContainer}>
        <Label type="greenBig">Are you sure you want to send this for review?</Label>
        <Label type="modalBody">
          This will lock the offer, and you won't be able to make changes until underwriting has reviewed it.
        </Label>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button variant="contained" color="secondary" className={classes.btn} onClick={onClose}>
          <Label className={classes.btnLabel}>No, cancel</Label>
        </Button>
        <Button variant="contained" color="primary" className={classes.btn} onClick={onConfirm}>
          <Label className={classes.btnLabel}>Yes, send to underwriting</Label>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FarmOfferFooter = observer(({ onContinueToCheckout, setShowValidationErrorsModal }) => {
  const { offer: store } = useStore();
  const classes = useStyles();
  const toast = useToast();
  const { canEditOffers, canApproveUWReview, user } = useSession();
  const username = user?.username;
  const { validateForm, setErrors, setTouched } = useFormikContext();
  const history = useHistory();
  const [showSendToUWModal, setShowSendToUWModal] = useState(false);

  const approvalDetails = store.offer?.quote?.preBindUWCheck?.approvalDetails;
  const sentToUnderwritingAt = approvalDetails?.sentToUnderwritingAt;
  const underwriterApprovedAt = approvalDetails?.underwriterApprovedAt;
  const underwriterApprovedBy = approvalDetails?.underwriterApprovedBy;
  const isApproved = underwriterApprovedAt && underwriterApprovedBy;

  const goToUWForm = useCallback(async () => {
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);
      setTouched(updateErrors);
      setShowValidationErrorsModal(true);
    } else {
      history.push(`/offer/${store.offer.id}/verifyFarmUW`);
    }
  }, [history.push, setShowValidationErrorsModal, validateForm, setErrors, setTouched, store.offer.id]);

  const farmOnSendToUW = useCallback(async () => {
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched(validationErrors);
      setShowValidationErrorsModal(true);
      return;
    }

    const sentToUnderwritingAt = new Date().toISOString();
    const sentToUnderwritingBy = username;

    // Single call that handles both updating the offer and sending the notification
    const result = await store.sendOfferForUnderwritingApproval(
      store.offer.id,
      sentToUnderwritingAt,
      sentToUnderwritingBy,
      history
    );

    if (result.ok) {
      toast.notify({
        type: 'success',
        message:
          "Your offer is on its way to our underwriting team. You'll hear back within one business day—we'll keep you posted along the way."
      });
    }
  }, [validateForm, setErrors, setTouched, setShowValidationErrorsModal, username, store, toast, history]);

  const farmUWApproveForCheckout = useCallback(async () => {
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched(validationErrors);
      setShowValidationErrorsModal(true);
      return;
    }

    const uwApprovalDetails = {
      underwriterApprovedAt: new Date().toISOString(),
      underwriterApprovedBy: username
    };

    await store.uwApproveForCheckout(store.offer.id, uwApprovalDetails, history);

    if (!store.errors?.length) {
      toast.notify({
        type: 'success',
        message: "You've approved this offer for checkout."
      });
    }
  }, [validateForm, setErrors, setTouched, setShowValidationErrorsModal, username, store, history, toast]);

  if (canApproveUWReview) {
    return (
      <>
        <div>
          <Label className={classes.title} type="subtitle">
            Your offer is ready
          </Label>
        </div>
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers}
              onClick={goToUWForm}
            >
              <img src={viewTask} alt="" className={classes.btnIcon} />
              <Label className={classes.btnLabel}>Review underwriting</Label>
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers || isApproved}
              onClick={farmUWApproveForCheckout}
            >
              <img src={documentApprovalIcon} alt="" className={classes.btnIcon} />
              <Label className={classes.btnLabel}>Approve for checkout</Label>
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers || !isApproved}
              onClick={onContinueToCheckout}
            >
              <Label className={classes.btnLabel}>Continue to checkout</Label>
            </Button>
          </Grid>
        </Grid>
      </>
    );
  }

  // Default: user does not have farm underwriting approval permission
  if (store.needsPreBindUWForm) {
    return (
      <FarmUnderwritingReviewPageFooter
        setShowValidationErrorsModal={setShowValidationErrorsModal}
        goToUWForm={goToUWForm}
      />
    );
  }

  return (
    <>
      <div>
        <Label className={classes.title} type="subtitle">
          Your offer is ready
        </Label>
      </div>
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        {!sentToUnderwritingAt && (
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              disabled={store.loading || !canEditOffers}
              onClick={() => setShowSendToUWModal(true)}
            >
              <Label className={classes.btnLabel}>Send to underwriting</Label>
            </Button>
          </Grid>
        )}

        <Grid item>
          <Button
            variant="contained"
            color="primary"
            className={classes.btn}
            disabled={store.loading || !canEditOffers || !isApproved}
            onClick={onContinueToCheckout}
          >
            <Label className={classes.btnLabel}>Continue to checkout</Label>
          </Button>
        </Grid>
      </Grid>

      <SendToUnderwritingConfirmModal
        open={showSendToUWModal}
        onClose={() => setShowSendToUWModal(false)}
        onConfirm={() => {
          setShowSendToUWModal(false);
          farmOnSendToUW();
        }}
      />
    </>
  );
});

FarmOfferFooter.propTypes = {
  onContinueToCheckout: PropTypes.func.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired
};

export default FarmOfferFooter;
